// client/src/coms/settings/sections/AccountSettings.js
// Personal Settings → My Account: avatar, account switching and signed-in devices.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Section } from '../../personalisation/settingsControls';
import SignedInDevices from '../../accounts/SignedInDevices';
import { notify } from '../../organisation/orgShared';

export default function AccountSettings({ user }) {
  const [avatarUrl, setAvatarUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/coms/avatar', { credentials: 'include', cache: 'no-store' });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data || !data.ok) throw new Error((data && data.error) || `Could not load your avatar (${res.status}).`);
        if (!cancelled) setAvatarUrl(data.avatar_url ? String(data.avatar_url) : '');
      } catch (e) {
        if (!cancelled) setError(String((e && e.message) || 'Could not load your avatar.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type || '')) {
      setError('Choose an image file (PNG, JPEG, GIF or WebP).');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await fetch('/api/coms/avatar', { method: 'POST', body: fd, credentials: 'include' });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.ok) {
        throw new Error(res.status === 413 ? 'That image is too large. Try one under 8 MB.' : (data && data.error) || `Upload failed (${res.status}).`);
      }
      const raw = data.avatar_url ? String(data.avatar_url) : '';
      setAvatarUrl(raw ? `${raw}${raw.includes('?') ? '&' : '?'}v=${Date.now()}` : '');
      notify('Avatar updated.', { tone: 'success' });
    } catch (e) {
      setError(String((e && e.message) || 'Could not upload your avatar.'));
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, []);

  const name = String((user && (user.username || user.name)) || '').trim();

  return (
    <Section id="vx-cat-account" title="My Account" description="Your avatar and where you are signed in. Your name comes from your sign-in account.">
      <Row label="Avatar" hint="A square image works best; it is resized and optimised when uploaded." settingId="avatar" scope="account">
        <div className="vx-avatar-edit">
          <span className="vx-avatar-big" aria-hidden={!avatarUrl}>
            {avatarUrl ? <img src={avatarUrl} alt="Your avatar" /> : <span>{(name || '?').charAt(0).toUpperCase()}</span>}
          </span>
          <div className="vx-inline">
            <button type="button" className="vx-btn small" disabled={loading} onClick={() => fileRef.current && fileRef.current.click()} data-testid="avatar-upload">
              {loading ? 'Working…' : avatarUrl ? 'Change avatar' : 'Upload avatar'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files && e.target.files[0])} />
          </div>
        </div>
        {error ? (
          <div className="vx-acct-error" role="alert">
            {error}
          </div>
        ) : null}
      </Row>
      {name ? (
        <Row label="Signed in as" hint="Use the account menu on your profile bar to add or switch accounts on this device." settingId="accounts" scope="device">
          <span className="vx-set-value">{name}</span>
        </Row>
      ) : null}
      <div id="vx-setting-devices" data-setting="devices" className="vx-set-legacy">
        <SignedInDevices />
      </div>
    </Section>
  );
}
